import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { HardDrive, Settings, Plus, X, Edit2, Trash2, Building2, Tag } from 'lucide-react'
import {
  getAllStorageLimits,
  getTierStorageLimits,
  setTierStorageLimits,
  setOrganizationStorageLimits,
  removeOrganizationStorageLimits,
  getAllOrganizationsStorageUsage,
} from '../services/masterAdminStorage'
import { formatBytes } from '../services/storageManagement'
import { supabase } from '../services/supabase'
import Card from './Card'
import Button from './Button'
import Input from './Input'
import Select from './Select'

const MasterAdminStorageLimits = () => {
  const [limits, setLimits] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [tiers, setTiers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showTierForm, setShowTierForm] = useState(false)
  const [showOrgForm, setShowOrgForm] = useState(false)
  const [editingTier, setEditingTier] = useState(null)
  const [editingOrg, setEditingOrg] = useState(null)
  const [formData, setFormData] = useState({
    maxStorageGB: '',
    maxFileSizeMB: '',
    maxFiles: '',
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [limitsResult, orgsResult] = await Promise.all([
        getAllStorageLimits(),
        getAllOrganizationsStorageUsage(),
      ])

      if (limitsResult.success) {
        setLimits(limitsResult.data || [])
      }

      if (orgsResult.success) {
        setOrganizations(orgsResult.data || [])
      }

      // Load tiers
      const { data: tiersData } = await supabase.from('subscription_tiers').select('*').order('price_monthly')
      setTiers(tiersData || [])
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveTierLimit = async () => {
    try {
      const tierId = editingTier || formData.tierId
      if (!tierId) {
        alert('Please select a tier')
        return
      }

      await setTierStorageLimits(tierId, {
        maxStorageBytes: parseFloat(formData.maxStorageGB) * 1024 * 1024 * 1024,
        maxFileSizeBytes: parseFloat(formData.maxFileSizeMB) * 1024 * 1024,
        maxFilesPerOrganization: formData.maxFiles ? parseInt(formData.maxFiles) : null,
      })

      setShowTierForm(false)
      setEditingTier(null)
      setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
      loadData()
    } catch (error) {
      console.error('Error saving tier limit:', error)
      alert('Failed to save tier limit')
    }
  }

  const handleSaveOrgLimit = async () => {
    try {
      const orgId = editingOrg || formData.organizationId
      if (!orgId) {
        alert('Please select an organization')
        return
      }

      await setOrganizationStorageLimits(orgId, {
        maxStorageBytes: parseFloat(formData.maxStorageGB) * 1024 * 1024 * 1024,
        maxFileSizeBytes: parseFloat(formData.maxFileSizeMB) * 1024 * 1024,
        maxFilesPerOrganization: formData.maxFiles ? parseInt(formData.maxFiles) : null,
      })

      setShowOrgForm(false)
      setEditingOrg(null)
      setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
      loadData()
    } catch (error) {
      console.error('Error saving organization limit:', error)
      alert('Failed to save organization limit')
    }
  }

  const handleEditTier = async (tierId) => {
    const tierLimit = limits.find((l) => l.subscription_tier_id === tierId && !l.organization_id)
    if (tierLimit) {
      setFormData({
        maxStorageGB: (tierLimit.max_storage_bytes / (1024 * 1024 * 1024)).toFixed(2),
        maxFileSizeMB: (tierLimit.max_file_size_bytes / (1024 * 1024)).toFixed(2),
        maxFiles: tierLimit.max_files_per_organization?.toString() || '',
      })
      setEditingTier(tierId)
      setShowTierForm(true)
    } else {
      setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
      setEditingTier(tierId)
      setShowTierForm(true)
    }
  }

  const handleEditOrg = async (orgId) => {
    const orgLimit = limits.find((l) => l.organization_id === orgId)
    if (orgLimit) {
      setFormData({
        maxStorageGB: (orgLimit.max_storage_bytes / (1024 * 1024 * 1024)).toFixed(2),
        maxFileSizeMB: (orgLimit.max_file_size_bytes / (1024 * 1024)).toFixed(2),
        maxFiles: orgLimit.max_files_per_organization?.toString() || '',
      })
      setEditingOrg(orgId)
      setShowOrgForm(true)
    } else {
      setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
      setEditingOrg(orgId)
      setShowOrgForm(true)
    }
  }

  const handleRemoveOrgLimit = async (orgId) => {
    if (!confirm('Remove custom storage limit for this organization? It will revert to tier default.')) {
      return
    }

    try {
      await removeOrganizationStorageLimits(orgId)
      loadData()
    } catch (error) {
      console.error('Error removing organization limit:', error)
      alert('Failed to remove organization limit')
    }
  }

  const tierLimits = limits.filter((l) => l.subscription_tier_id && !l.organization_id)
  const orgLimits = limits.filter((l) => l.organization_id)

  if (loading) {
    return <div className="text-sm text-gray-500">Loading storage limits...</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HardDrive size={24} />
            Storage Limits Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure storage limits per subscription tier and customize limits for specific organizations
          </p>
        </div>
      </div>

      {/* Tier Limits Section */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Tag size={20} />
            Subscription Tier Limits
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditingTier(null)
              setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
              setShowTierForm(true)
            }}
          >
            <Plus size={16} />
            Add Tier Limit
          </Button>
        </div>

        {tierLimits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No tier limits configured. Default limits apply.</p>
        ) : (
          <div className="space-y-3">
            {tierLimits.map((limit) => {
              const tier = tiers.find((t) => t.id === limit.subscription_tier_id)
              return (
                <div
                  key={limit.id}
                  className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-between"
                >
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{tier?.name || 'Unknown Tier'}</p>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-x-4">
                      <span>Max Storage: {formatBytes(limit.max_storage_bytes)}</span>
                      <span>Max File Size: {formatBytes(limit.max_file_size_bytes)}</span>
                      {limit.max_files_per_organization && (
                        <span>Max Files: {limit.max_files_per_organization.toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => handleEditTier(limit.subscription_tier_id)}>
                    <Edit2 size={16} />
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Organization Overrides Section */}
      <Card variant="soft" className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Building2 size={20} />
            Organization Custom Limits
          </h3>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setEditingOrg(null)
              setFormData({ maxStorageGB: '', maxFileSizeMB: '', maxFiles: '' })
              setShowOrgForm(true)
            }}
          >
            <Plus size={16} />
            Add Custom Limit
          </Button>
        </div>

        {orgLimits.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No custom organization limits set.</p>
        ) : (
          <div className="space-y-3">
            {orgLimits.map((limit) => {
              const orgUsage = organizations.find((o) => o.organization_id === limit.organization_id)
              const usagePercent = limit.max_storage_bytes > 0
                ? ((orgUsage?.total_bytes_used || 0) / limit.max_storage_bytes) * 100
                : 0

              return (
                <div
                  key={limit.id}
                  className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {limit.organizations?.name || 'Unknown Organization'}
                      </p>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-x-4">
                        <span>Max Storage: {formatBytes(limit.max_storage_bytes)}</span>
                        <span>Max File Size: {formatBytes(limit.max_file_size_bytes)}</span>
                        {limit.max_files_per_organization && (
                          <span>Max Files: {limit.max_files_per_organization.toLocaleString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditOrg(limit.organization_id)}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRemoveOrgLimit(limit.organization_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  {orgUsage && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-500">Usage</span>
                        <span className="text-xs font-semibold">{usagePercent.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            usagePercent >= 100 ? 'bg-red-500' : usagePercent >= 80 ? 'bg-yellow-500' : 'bg-primary-500'
                          }`}
                          style={{ width: `${Math.min(100, usagePercent)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatBytes(orgUsage.total_bytes_used)} / {formatBytes(limit.max_storage_bytes)} •{' '}
                        {orgUsage.total_files_count} files
                      </p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* Tier Limit Form Modal */}
      {showTierForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingTier ? 'Edit' : 'Add'} Tier Storage Limit
              </h3>
              <button onClick={() => setShowTierForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {!editingTier && (
                <Select
                  label="Subscription Tier"
                  options={tiers.map((t) => ({ value: t.id, label: t.name }))}
                  value={formData.tierId || ''}
                  onChange={(e) => setFormData({ ...formData, tierId: e.target.value })}
                  required
                />
              )}
              <Input
                label="Max Storage (GB)"
                type="number"
                step="0.1"
                value={formData.maxStorageGB}
                onChange={(e) => setFormData({ ...formData, maxStorageGB: e.target.value })}
                placeholder="e.g., 10"
                required
              />
              <Input
                label="Max File Size (MB)"
                type="number"
                step="0.1"
                value={formData.maxFileSizeMB}
                onChange={(e) => setFormData({ ...formData, maxFileSizeMB: e.target.value })}
                placeholder="e.g., 10"
                required
              />
              <Input
                label="Max Files (Optional - leave empty for unlimited)"
                type="number"
                value={formData.maxFiles}
                onChange={(e) => setFormData({ ...formData, maxFiles: e.target.value })}
                placeholder="e.g., 1000"
              />
              <div className="flex gap-2">
                <Button onClick={handleSaveTierLimit} className="flex-1">
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setShowTierForm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Organization Limit Form Modal */}
      {showOrgForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingOrg ? 'Edit' : 'Add'} Organization Storage Limit
              </h3>
              <button onClick={() => setShowOrgForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              {!editingOrg && (
                <Select
                  label="Organization"
                  options={organizations.map((o) => ({
                    value: o.organization_id,
                    label: o.organizations?.name || `Organization ${o.organization_id}`,
                  }))}
                  value={formData.organizationId || ''}
                  onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                  required
                />
              )}
              <Input
                label="Max Storage (GB)"
                type="number"
                step="0.1"
                value={formData.maxStorageGB}
                onChange={(e) => setFormData({ ...formData, maxStorageGB: e.target.value })}
                placeholder="e.g., 10"
                required
              />
              <Input
                label="Max File Size (MB)"
                type="number"
                step="0.1"
                value={formData.maxFileSizeMB}
                onChange={(e) => setFormData({ ...formData, maxFileSizeMB: e.target.value })}
                placeholder="e.g., 10"
                required
              />
              <Input
                label="Max Files (Optional - leave empty for unlimited)"
                type="number"
                value={formData.maxFiles}
                onChange={(e) => setFormData({ ...formData, maxFiles: e.target.value })}
                placeholder="e.g., 1000"
              />
              <div className="flex gap-2">
                <Button onClick={handleSaveOrgLimit} className="flex-1">
                  Save
                </Button>
                <Button variant="secondary" onClick={() => setShowOrgForm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default MasterAdminStorageLimits

